import { Link } from "react-router-dom";
import { ArrowUpRight, ChatCircleText, EnvelopeSimple, MapPin, Phone, WhatsappLogo } from "@phosphor-icons/react";
import PublicHeader from "@/components/PublicHeader";
import Footer from "@/components/Footer";
import { SMS_HREF, TEXT_NUMBER, WHATSAPP_HREF } from "@/components/ContactChannels";

const SISTER = [
  {
    name: "Yellow Thinking",
    href: "https://www.yellowthinking.com",
    blurb: "Branding, websites, social, print, TV and cinema — the parent creative studio.",
  },
  {
    name: "fasterbetter.ca",
    href: "https://www.fasterbetter.ca",
    blurb: "Themeless WordPress builds and managed cloud hosting tuned for speed.",
  },
  {
    name: "webcoders.ca",
    href: "https://www.webcoders.ca",
    blurb: "Custom website development when a template will not do.",
  },
];

export default function ContactPage() {
  return (
    <div className="min-h-screen bg-background">
      <PublicHeader active="contact" />

      <section className="border-b border-border">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-16 lg:py-24">
          <div className="max-w-3xl">
            <div className="label-overline mb-4">Contact</div>
            <h1 className="font-display text-5xl lg:text-6xl font-black tracking-tighter leading-[1] mb-6">
              Pick up the phone. <br />
              <span className="text-brand">We will pick up too.</span>
            </h1>
            <p className="text-lg text-ink-muted leading-relaxed max-w-2xl">
              No chatbots, no contact forms that vanish into a CRM. If you want
              to talk about an audit, a fix, a bigger project, or just want a
              second opinion before you commit — call us, text us on{" "}
              <span className="text-ink font-medium">{TEXT_NUMBER}</span>, or
              ping us on WhatsApp. A real person answers, Monday to Friday,
              Toronto time.
            </p>
          </div>
        </div>
      </section>

      <section className="border-b border-border bg-white">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-16 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7 space-y-6">
            <ContactRow
              icon={Phone}
              label="Call"
              testId="contact-phone-row"
              primary={
                <a href="tel:+18446061100" data-testid="contact-phone-link"
                  className="text-3xl lg:text-4xl font-display font-black tracking-tight text-ink hover:text-brand transition-colors">
                  1.844.606.1100
                </a>
              }
              secondary="Toronto · Monday–Friday · 9 AM – 6 PM ET"
            />

            <div className="grid sm:grid-cols-2 gap-4">
              <ContactRow
                icon={ChatCircleText}
                label="Text"
                testId="contact-sms-row"
                primary={
                  <a href={SMS_HREF} data-testid="contact-sms-link"
                    className="text-xl font-display font-bold tracking-tight text-ink hover:text-brand transition-colors">
                    Send an SMS
                  </a>
                }
                secondary={`Direct line · ${TEXT_NUMBER}`}
              />
              <ContactRow
                icon={WhatsappLogo}
                label="WhatsApp"
                testId="contact-whatsapp-row"
                primary={
                  <a href={WHATSAPP_HREF} target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp-link"
                    className="text-xl font-display font-bold tracking-tight text-ink hover:text-brand transition-colors">
                    Chat on WhatsApp
                  </a>
                }
                secondary={`Same line · ${TEXT_NUMBER}`}
              />
            </div>

            <ContactRow
              icon={EnvelopeSimple}
              label="Write"
              testId="contact-email-row"
              primary={
                <a href="mailto:hello@auditmysite.ca" data-testid="contact-email-link"
                  className="text-xl lg:text-2xl font-display font-bold tracking-tight text-ink hover:text-brand transition-colors break-all">
                  hello@auditmysite.ca
                </a>
              }
              secondary="Replies usually inside one business day."
            />
            <ContactRow
              icon={MapPin}
              label="Visit"
              testId="contact-address-row"
              primary={
                <div className="text-xl font-display font-bold tracking-tight text-ink">
                  Toronto, Canada
                </div>
              }
              secondary="Studio visits are by appointment — please call ahead."
            />
          </div>

          <div className="lg:col-span-5">
            <div className="surface-card hard-shadow-sm p-6 lg:p-8">
              <div className="label-overline mb-3">Sister studios</div>
              <p className="text-sm text-ink-muted mb-5 leading-relaxed">
                Auditor is part of the Yellow Thinking group. If your project is
                bigger than an audit — a new website, a speed overhaul, or
                custom development — these are the people to call.
              </p>
              <ul className="space-y-4">
                {SISTER.map((s) => (
                  <li key={s.href}>
                    <a
                      href={s.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`contact-sister-${s.name.replace(/[^a-z]/gi, "").toLowerCase()}`}
                      className="group block"
                    >
                      <div className="flex items-center gap-1 font-display font-bold text-lg text-ink group-hover:text-brand transition-colors">
                        {s.name} <ArrowUpRight size={14} weight="bold" className="opacity-60 group-hover:opacity-100" />
                      </div>
                      <div className="text-xs text-ink-muted">{s.blurb}</div>
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-ink text-white">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-16 grid lg:grid-cols-2 gap-8 items-center">
          <div>
            <div className="label-overline text-white/60 mb-4">Quick action</div>
            <h2 className="font-display text-3xl lg:text-4xl font-black tracking-tight leading-tight">
              Already know what you need? <br /> Start an audit while we talk.
            </h2>
          </div>
          <div className="lg:justify-self-end">
            <Link to="/register" data-testid="contact-cta-register"
              className="inline-flex items-center gap-2 bg-white text-ink px-6 py-4 rounded-sm font-semibold hover:bg-brand hover:text-white transition-colors">
              Start a free audit
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

function ContactRow({ icon: Icon, label, primary, secondary, testId }) {
  return (
    <div className="surface-card p-6 flex items-start gap-5" data-testid={testId}>
      <div className="w-12 h-12 rounded-sm border border-ink flex items-center justify-center shrink-0">
        <Icon size={22} weight="duotone" className="text-brand" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="label-overline mb-1">{label}</div>
        {primary}
        <div className="text-xs text-ink-muted mt-2">{secondary}</div>
      </div>
    </div>
  );
}
